/**
 * **Peran POS** — dan sebelum layar ini, tak ada satu pun cara membuatnya.
 *
 * Imron (2026-08-04): *"role belum ada"*.
 *
 * ### ⛔⛔ Layar Staf hanya bisa MEMILIH peran, tak pernah membuatnya
 * `Staff.tsx` menampilkan pemilih peran lalu mencentang izinnya. Di tenant baru `pos_role` kosong,
 * jadi pemilihnya kosong, jadi tak ada yang bisa dicentang — dan tak ada tombol di mana pun yang
 * menambah barisnya. Onboarding mandiri mentok total di situ: tanpa peran tak ada izin, tanpa izin
 * tak ada yang bisa membuka sesi kas.
 *
 * ### ⭐ Peran ↔ JABATAN, bukan peran ↔ orang
 * Izin menempel pada `pos_role`; staf menunjuk `position_code`; `position_role_map` menjodohkan
 * keduanya. Itu sebabnya menaikkan satu orang jadi penyelia tak menuntut menyentuh izin sama
 * sekali — jabatannya yang berubah. ⚠️ Peta jabatan→peran ikut disunting DI SINI, karena peran yang
 * tak dipetakan ke jabatan mana pun adalah peran yang izinnya tak pernah sampai ke siapa pun.
 */
import { Alert, Card, Checkbox, Form, Input, Select, Space, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { POS_PERMISSIONS } from '@osbpos/contract';

import { api } from '../api/client';
import { canWrite } from '../api/session';
import { CrudPanel } from '../components/CrudPanel';

interface Role {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}
interface RolePerm {
  readonly id: string;
  readonly pos_role_id: string;
  readonly permission_code: string;
}
interface PositionMap {
  readonly id: string;
  readonly position_code: string;
  readonly pos_role_id: string;
}

export function Roles() {
  const { t } = useTranslation();
  const boleh = canWrite();

  const [role, setRole] = useState<readonly Role[]>([]);
  const [perm, setPerm] = useState<readonly RolePerm[]>([]);
  const [peta, setPeta] = useState<readonly PositionMap[]>([]);
  const [dipilih, setDipilih] = useState<string | null>(null);
  const [jabatan, setJabatan] = useState('');
  const [tick, setTick] = useState(0);

  const muat = useCallback(() => api.listMaster<Role>('roles'), [tick]);

  const muatSisanya = useCallback(() => {
    void api.listMaster<RolePerm>('role-permissions').then(setPerm).catch(() => undefined);
    void api.listMaster<PositionMap>('position-roles').then(setPeta).catch(() => undefined);
    void api.listMaster<Role>('roles').then(setRole).catch(() => undefined);
  }, []);
  useEffect(() => { muatSisanya(); }, [muatSisanya, tick]);

  const kolom: ColumnsType<Role> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 200,
      render: (v: string) => <b data-testid={`peran-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      /* ⭐ Jumlah izin tampil DI TABEL: peran tanpa izin terlihat sama persis dengan peran yang
         lengkap kalau angkanya disembunyikan di dalam laci — dan peran kosong adalah penyebab
         "kasir tak bisa apa-apa" yang paling sering. */
      title: t('roles.permCount'),
      key: 'izin',
      width: 140,
      render: (_: unknown, r: Role) => {
        const n = perm.filter((p) => p.pos_role_id === r.id).length;
        return n === 0
          ? <Tag color="orange" data-testid={`peran-kosong-${r.code}`}>{t('roles.noPerm')}</Tag>
          : <Tag color="green" data-testid={`peran-izin-${r.code}`}>{t('roles.nPerm', { n })}</Tag>;
      },
    },
    {
      title: t('roles.positions'),
      key: 'jabatan',
      width: 240,
      render: (_: unknown, r: Role) => {
        const j = peta.filter((p) => p.pos_role_id === r.id);
        return j.length === 0
          ? <Tag data-testid={`peran-tanpa-jabatan-${r.code}`}>{t('roles.noPosition')}</Tag>
          : <>{j.map((x) => <Tag key={x.id}>{x.position_code}</Tag>)}</>;
      },
    },
  ];

  async function ubahIzin(roleId: string, kode: string, on: boolean): Promise<void> {
    try {
      const ada = perm.find((p) => p.pos_role_id === roleId && p.permission_code === kode);
      if (on && ada === undefined) {
        await api.createMaster('role-permissions', { posRoleId: roleId, permissionCode: kode });
      } else if (!on && ada !== undefined) {
        await api.deleteMaster('role-permissions', ada.id);
      }
      muatSisanya();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  }

  async function tambahJabatan(): Promise<void> {
    if (dipilih === null || jabatan.trim() === '') return;
    try {
      await api.createMaster('position-roles', {
        positionCode: jabatan.trim().toUpperCase(),
        posRoleId: dipilih,
      });
      setJabatan('');
      muatSisanya();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    }
  }

  const aktif = role.find((r) => r.id === dipilih) ?? null;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="roles">
      {/* ⚠️ Menyebut urutannya: peran → izin → jabatan → staf. Tanpa itu orang membuat staf lebih
          dulu lalu menemukan tak ada peran yang bisa dipilih, dan menyimpulkan layarnya rusak. */}
      <Alert type="info" showIcon title={t('roles.note')} data-testid="peran-catatan" />

      <CrudPanel<Role>
        testId="peran"
        title={t('roles.title')}
        subtitle={t('roles.subtitle')}
        canWrite={boleh}
        newLabel={t('roles.new')}
        columns={kolom}
        fetchList={muat}
        searchIn={(r) => `${r.code} ${r.name}`}
        emptyText={t('roles.empty')}
        renderFields={(_f, editing) => (
          <>
            {editing === null ? (
              <Form.Item
                name="code"
                label={t('master.code')}
                rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                tooltip={t('structure.codeFixed')}
              >
                <Input maxLength={40} data-testid="peran-kode" />
              </Form.Item>
            ) : (
              <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t('structure.codeLocked')} />
            )}
            <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
              <Input maxLength={120} data-testid="peran-nama" />
            </Form.Item>
            {/* ⚠️ Izin & jabatan disunting di kartu bawah, bukan di laci: keduanya berlaku SEKETIKA
                (tabel kaitan), sementara isi laci baru tersimpan saat Simpan ditekan. Menyatukannya
                membuat satu laci punya dua makna "tersimpan". */}
            <Alert type="info" showIcon title={t('roles.permBelow')} />
          </>
        )}
        toFormValues={(r) => ({ name: r.name })}
        onCreate={async (v) => {
          await api.createMaster('roles', {
            code: String(v['code']).toUpperCase(),
            name: String(v['name']),
          });
          setTick((x) => x + 1);
        }}
        onUpdate={async (id, v) => {
          await api.updateMaster('roles', id, { name: v['name'] });
          setTick((x) => x + 1);
        }}
      />

      <Card
        size="small"
        data-testid="peran-izin-kartu"
        title={aktif === null ? t('roles.pickRole') : `${t('roles.permOf')} · ${aktif.code}`}
        extra={
          <Select<string>
            style={{ minWidth: 240 }}
            allowClear
            placeholder={t('roles.pickRole')}
            data-testid="peran-pilih"
            {...(dipilih === null ? {} : { value: dipilih })}
            onChange={(v) => { setDipilih(v ?? null); }}
            options={role.map((r) => ({ value: r.id, label: `${r.code} — ${r.name}` }))}
          />
        }
      >
        {aktif === null ? (
          <Typography.Text type="secondary">{t('roles.pickRole')}</Typography.Text>
        ) : (
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            {/*
              ⛔ Daftar izin diambil dari KONTRAK, tak pernah disalin ke sini. Salinan menua
              diam-diam: izin baru di `packages/contract` takkan pernah muncul di layar, dan tak
              ada yang memerah — persis yang terjadi pada `payment:take` di skrip dev.
            */}
            <Space wrap>
              {POS_PERMISSIONS.map((kode) => (
                <Checkbox
                  key={kode}
                  disabled={!boleh}
                  data-testid={`izin-${kode}`}
                  checked={perm.some(
                    (p) => p.pos_role_id === aktif.id && p.permission_code === kode,
                  )}
                  onChange={(e) => { void ubahIzin(aktif.id, kode, e.target.checked); }}
                >
                  {kode}
                </Checkbox>
              ))}
            </Space>

            <Typography.Text strong style={{ marginTop: 12 }}>{t('roles.positions')}</Typography.Text>
            {/* ⭐ Jabatan yang menghubungkan peran ke ORANG. Peran tanpa jabatan = izin yang tak
                pernah sampai ke siapa pun, dan itu tak terlihat dari daftar staf. */}
            <Alert type="info" showIcon title={t('roles.positionHint')} />
            <Space>
              <Input
                style={{ width: 220 }}
                placeholder="SUPERVISOR"
                value={jabatan}
                disabled={!boleh}
                data-testid="jabatan-kode"
                onChange={(e) => { setJabatan(e.target.value); }}
                onPressEnter={() => void tambahJabatan()}
              />
            </Space>
            <Table<PositionMap>
              size="small"
              pagination={false}
              rowKey={(p) => p.id}
              dataSource={peta.filter((p) => p.pos_role_id === aktif.id).slice()}
              locale={{ emptyText: <span data-testid="jabatan-kosong">{t('roles.noPosition')}</span> }}
              columns={[
                {
                  title: t('staff.position'),
                  render: (_: unknown, p: PositionMap) => (
                    <b data-testid={`jabatan-${p.position_code}`}>{p.position_code}</b>
                  ),
                },
              ]}
            />
          </Space>
        )}
      </Card>
    </Space>
  );
}
